import { useEffect, useState } from 'react';
import { createRoute, redirect, useParams, useNavigate, Link } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../../__root';
import { Layout } from '@/client/layouts/layout';
import {
  DetailHeader,
  DetailCard,
  DetailGrid,
  DetailList,
  DetailRow,
  CopyableText,
} from '@/client/components/ui/detail-view';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { EncoderIDCell } from '@/client/components/tables/cells';
import { StatusBadge } from '@/client/components/tables/live-streams-columns';
import { formatDistanceToNow, formatDistanceToNowStrict } from 'date-fns';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import { Progress } from '@/client/components/ui/progress';
import { RefreshCw, Loader2, Radio } from 'lucide-react';
import { API_BASE_URL } from '@/client/config';

type ServerType = 'ingress' | 'egress' | 'transcode' | 'unknown';

interface DBServerInfo {
  serverType: ServerType;
  dbStatus?: string | null;
  timestamp?: string | null;
  sourceConnectionCount?: number | null;
  maxSources?: number | null;
  maxSlots?: number | null;
  freeSlots?: number | null;
  sysType?: string | null;
}

interface EC2Instance {
  instanceId: string;
  name: string | null;
  instanceType: string | null;
  state: string | null;
  launchTime: string | null;
  privateIp: string | null;
  publicIp: string | null;
  availabilityZone: string | null;
  serverType: ServerType;
  dbInfo: DBServerInfo | null;
}

interface EC2Metrics {
  instanceId: string;
  cpu?: number;
}

interface EC2InstanceWithMetrics extends EC2Instance {
  metrics?: EC2Metrics;
}

interface ActiveStream {
  liveID: string | null;
  clientID: string | null;
  clientName: string | null;
  eventID: string | null;
  eventTitle: string | null;
  status: string | null;
  streamStatus: string | null;
  eventStartDateTime: string | null;
  startDateTime: string | null;
  encoderID: string | null;
  ingInstanceID: string | null;
  egInstanceID: string | null;
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'aws/ec2/$instanceId',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: EC2DetailComponent,
});

function StateBadge({ state }: { state: string | null }) {
  if (!state) return <span className="text-muted-foreground">-</span>;

  const variant = state === 'running' ? 'default' :
                  state === 'stopped' ? 'secondary' :
                  ['terminated', 'shutting-down'].includes(state) ? 'destructive' :
                  'outline';

  return (
    <Badge variant={variant} className={state === 'running' ? 'bg-green-600' : ''}>
      {state}
    </Badge>
  );
}

function ServerTypeBadge({ type }: { type: ServerType }) {
  const colors: Record<ServerType, string> = {
    ingress: 'bg-blue-600',
    egress: 'bg-purple-600',
    transcode: 'bg-orange-600',
    unknown: 'bg-gray-500',
  };

  return (
    <Badge className={colors[type]}>
      {type}
    </Badge>
  );
}

function CPUCell({ cpu }: { cpu?: number }) {
  if (cpu === undefined) return <span className="text-muted-foreground">-</span>;

  const color = cpu > 80 ? 'bg-red-500' : cpu > 60 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="flex items-center gap-2">
      <Progress value={cpu} className="h-2 w-16" indicatorClassName={color} />
      <span className="text-sm">{cpu}%</span>
    </div>
  );
}

function EC2DetailComponent() {
  const { instanceId } = useParams({ from: '/aws/ec2/$instanceId' });
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const [instance, setInstance] = useState<EC2InstanceWithMetrics | null>(null);
  const [activeStreams, setActiveStreams] = useState<ActiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();

      if (forceRefresh) {
        await Promise.all([
          fetch(`${API_BASE_URL}/api/cache/invalidate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'aws:ec2:instances' }),
          }),
          fetch(`${API_BASE_URL}/api/cache/invalidate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'aws:ec2:metrics' }),
          }),
        ]);
      }

      const [instancesRes, metricsRes, streamsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/aws/ec2/instances`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/aws/ec2/metrics`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/aws/ec2/${instanceId}/streams`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!instancesRes.ok) {
        const err = await instancesRes.json();
        throw new Error(err.message || 'Failed to fetch EC2 instances');
      }

      const instancesData = await instancesRes.json();
      const metricsData = metricsRes.ok ? await metricsRes.json() : { metrics: [] };
      const streamsData = streamsRes.ok ? await streamsRes.json() : { streams: [] };
      setActiveStreams(streamsData.streams || []);

      // Find the matching instance
      const foundInstance = instancesData.instances.find(
        (i: EC2Instance) => i.instanceId === instanceId
      );

      if (foundInstance) {
        const metricsForInstance = metricsData.metrics.find(
          (m: EC2Metrics) => m.instanceId === instanceId
        );
        setInstance({
          ...foundInstance,
          metrics: metricsForInstance,
        });
      } else {
        setError('Instance not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [instanceId, getAccessTokenSilently]);

  // Loading state
  if (loading && !instance) {
    return (
      <Layout>
        <DetailHeader
          title="Loading..."
          backTo="/aws/ec2-health"
          backLabel="Back to EC2 Health"
        />
        <div className="rounded-lg border p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-4">Loading instance data...</p>
        </div>
      </Layout>
    );
  }

  // Not found state
  if (!instance || error) {
    return (
      <Layout>
        <DetailHeader
          title="Instance Not Found"
          backTo="/aws/ec2-health"
          backLabel="Back to EC2 Health"
        />
        <div className="rounded-lg border p-8 text-center">
          <p className="text-muted-foreground mb-4">
            {error || 'This EC2 instance could not be found.'}
          </p>
          <Button variant="outline" onClick={() => navigate({ to: '/aws/ec2-health' })}>
            Return to EC2 Health
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <DetailHeader
        title={instance.name ?? 'Unnamed Instance'}
        subtitle={<CopyableText text={instance.instanceId} />}
        backTo="/aws/ec2-health"
        backLabel="Back to EC2 Health"
        badge={<StateBadge state={instance.state} />}
        actions={
          <Button
            variant="ghost"
            size="icon"
            title="Refresh"
            onClick={() => fetchData(true)}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        }
      />

      <DetailGrid columns={2}>
        {/* Status */}
        <DetailCard title="Status">
          <DetailList>
            <DetailRow label="State" value={<StateBadge state={instance.state} />} />
            <DetailRow label="Server Type" value={<ServerTypeBadge type={instance.serverType} />} />
            <DetailRow label="CPU" value={<CPUCell cpu={instance.metrics?.cpu} />} />
            <DetailRow
              label="Uptime"
              value={instance.launchTime
                ? formatDistanceToNow(new Date(instance.launchTime), { addSuffix: false })
                : '-'
              }
            />
          </DetailList>
        </DetailCard>

        {/* Instance Details */}
        <DetailCard title="Instance Details">
          <DetailList>
            <DetailRow
              label="Instance ID"
              value={<CopyableText text={instance.instanceId} />}
            />
            <DetailRow label="Instance Type" value={instance.instanceType} mono />
            <DetailRow label="Availability Zone" value={instance.availabilityZone} />
          </DetailList>
        </DetailCard>

        {/* Network */}
        <DetailCard title="Network">
          <DetailList>
            <DetailRow
              label="Private IP"
              value={instance.privateIp ? <CopyableText text={instance.privateIp} /> : '-'}
            />
            <DetailRow
              label="Public IP"
              value={instance.publicIp ? <CopyableText text={instance.publicIp} /> : '-'}
            />
          </DetailList>
        </DetailCard>

        {/* Database Info (if available) */}
        {instance.dbInfo && (
          <DetailCard title="Database Info">
            <DetailList>
              <DetailRow label="DB Status" value={instance.dbInfo.dbStatus ?? '-'} />
              {instance.dbInfo.serverType === 'ingress' && instance.dbInfo.maxSources && (
                <DetailRow
                  label="Sources"
                  value={`${instance.dbInfo.sourceConnectionCount ?? 0} / ${instance.dbInfo.maxSources}`}
                />
              )}
              {instance.dbInfo.serverType === 'transcode' && instance.dbInfo.maxSlots && (
                <DetailRow
                  label="Free Slots"
                  value={`${instance.dbInfo.freeSlots ?? 0} / ${instance.dbInfo.maxSlots}`}
                />
              )}
              {instance.dbInfo.serverType === 'egress' && instance.dbInfo.sysType && (
                <DetailRow label="System Type" value={instance.dbInfo.sysType} />
              )}
              {instance.dbInfo.timestamp && (
                <DetailRow
                  label="Last Updated"
                  value={formatDistanceToNow(new Date(instance.dbInfo.timestamp), { addSuffix: true })}
                />
              )}
            </DetailList>
          </DetailCard>
        )}
      </DetailGrid>

      {/* Active Streams Section */}
      {activeStreams.length > 0 && (
        <div className="mt-4">
          <div className="rounded-lg border-2 border-green-500/50 bg-green-500/5">
            <div className="flex items-center gap-2 px-4 py-3">
              <Radio className="h-4 w-4 text-green-500 animate-pulse" />
              <h3 className="font-medium text-sm text-green-600 dark:text-green-400">
                Active Stream{activeStreams.length > 1 ? 's' : ''} on This Instance
              </h3>
            </div>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow className="border-green-500/30 hover:bg-transparent">
                  <TableHead className="text-xs w-[25%]">Client</TableHead>
                  <TableHead className="text-xs w-[35%]">Event</TableHead>
                  <TableHead className="text-xs w-[10%]">Encoder</TableHead>
                  <TableHead className="text-xs w-[10%]">Status</TableHead>
                  <TableHead className="text-xs text-right w-[20%]">Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeStreams.map((stream, index) => (
                  <TableRow
                    key={stream.liveID || index}
                    className="border-green-500/20 hover:bg-green-500/5"
                  >
                    <TableCell className="py-2 truncate">
                      {stream.clientID && stream.clientName ? (
                        <Link
                          to="/clients/$clientID"
                          params={{ clientID: stream.clientID }}
                          className="text-xs hover:underline truncate block"
                          title={stream.clientName}
                        >
                          {stream.clientName}
                        </Link>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 truncate">
                      {stream.eventID && stream.clientID ? (
                        <Link
                          to="/events/$eventId"
                          params={{ eventId: stream.eventID }}
                          className="text-xs hover:underline truncate block"
                          title={stream.eventTitle || stream.eventID}
                        >
                          {stream.eventTitle || stream.eventID}
                        </Link>
                      ) : (
                        <span className="text-xs truncate">{stream.eventTitle || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      {stream.encoderID ? (
                        <EncoderIDCell encoderID={stream.encoderID} />
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <StatusBadge status={stream.status} />
                    </TableCell>
                    <TableCell className="py-2 text-right text-xs text-muted-foreground">
                      {stream.startDateTime
                        ? formatDistanceToNowStrict(new Date(stream.startDateTime), { addSuffix: true })
                        : '-'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Layout>
  );
}
